import type { FlexProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Image,
  LinkOverlay,
  Text,
} from '@chakra-ui/react';
import type { Banner } from '@cronos-app/cms';
import { MaskIcon } from '@ui/components/MaskIcon';
import {
  RenderInDesktop,
  RenderInMobile,
} from '@ui/components/MobileOrDesktop';
import { NoSSR } from '@ui/components/NoSSR';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { useBanners } from '@ui/hooks/cms/useBanners';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useRef, useState } from 'react';

const BANNER_DURATION_MS = 5000;

export const BannerContent = () => {
  const banners = useBanners().data;
  const switchedManually = useRef(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    watchDrag: () => {
      switchedManually.current = true;
      return true;
    },
  });
  const [selectedSlide, setSelectedSlide] = useState(0);

  const switchSlideManually = (index: number) => {
    switchedManually.current = true;
    emblaApi?.scrollTo(index);
  };

  useEffect(() => {
    if (!banners) return;
    const updateSlide = () => {
      setSelectedSlide(emblaApi ? emblaApi.selectedScrollSnap() : 0);
    };
    emblaApi?.on('select', updateSlide);
    const interval = setInterval(() => {
      if (switchedManually.current) return;
      setSelectedSlide((prevSlide) => {
        const nextSlide = (prevSlide + 1) % (banners.length + 1);
        emblaApi?.scrollTo(nextSlide);
        return nextSlide;
      });
    }, BANNER_DURATION_MS);

    return () => {
      clearInterval(interval);
      emblaApi?.off('select', updateSlide);
    };
  }, [emblaApi, banners]);

  return (
    <>
      <Box
        overflow="hidden"
        w={{ base: '335px', desktop: '668px' }}
        h={{ base: '186px', desktop: '114px' }}
        borderStyle="solid"
        outline="2px solid"
        outlineColor="primary"
        boxShadow="4px 4px 0px 0px {colors.primary}"
        rounded="12px"
        bgColor="background.rose"
        mt="16px"
        ref={emblaRef}
      >
        <Box display="flex">
          <Grid
            flex="0 0 100%"
            px="16px"
            py="12px"
            templateAreas={`"heading button" "main main"`}
            templateColumns="1fr auto"
            templateRows="repeat(2, auto)"
            position="relative"
          >
            <Text textStyle="body1Bold" gridArea="heading">
              How It Works ✧˖°
            </Text>
            <Box textStyle="body3" gridArea="main">
              <Flex gap="4px">
                <Text textStyle="body3Bold">1.</Text>
                <Text>
                  <Box as="span" textStyle="body3Bold">
                    Create:
                  </Box>{' '}
                  Create your agent’s unique personality.
                </Text>
              </Flex>
              <Flex gap="4px">
                <Text textStyle="body3Bold">2.</Text>
                <Text>
                  <Box as="span" textStyle="body3Bold">
                    Grow:
                  </Box>{' '}
                  Watch your agent gain popularity as the community buys its
                  token.
                </Text>
              </Flex>
              <Flex gap="4px">
                <Text textStyle="body3Bold">3.</Text>
                <Text>
                  <Box as="span" textStyle="body3Bold">
                    Graduate:
                  </Box>{' '}
                  When demand is reached your agent goes live on Telegram and H2
                  Finance.
                </Text>
              </Flex>
            </Box>
            <Button variant="icon" gridArea="button">
              <MaskIcon icon="url(/masks/arrow-outward.svg)" />
            </Button>
            <LinkOverlay href={EXTERNAL_LINK.gitbook} target="_blank" />
          </Grid>
          {banners?.map((banner, index) => (
            <Box position="relative" key={index} flex="0 0 100%">
              <NoSSR>
                <RenderInDesktop>
                  <Image
                    key="desktop"
                    src={banner.pathDesktop}
                    alt={`Banner ${index + 1}`}
                  />
                </RenderInDesktop>
                <RenderInMobile>
                  <Image
                    key="mobile"
                    src={banner.pathMobile}
                    alt={`Banner ${index + 1}`}
                  />
                </RenderInMobile>
                <LinkOverlay target="_blank" href={banner.link} />
              </NoSSR>
            </Box>
          ))}
        </Box>
      </Box>
      <BannerNavigation
        banners={banners}
        slideIndex={selectedSlide}
        switchSlide={switchSlideManually}
        mt="12px"
      />
    </>
  );
};

interface BannerNavigationProps extends FlexProps {
  banners: Banner[] | undefined;
  slideIndex: number;
  switchSlide: (index: number) => void;
}

const BannerNavigation: React.FC<BannerNavigationProps> = ({
  slideIndex,
  switchSlide,
  banners,
  ...flexProps
}) => {
  return (
    <Flex gap="4px" minH="18px" {...flexProps}>
      {Array.from({ length: banners ? banners.length + 1 : 0 }).map(
        (_, index) => (
          <Image
            key={index}
            w="24px"
            h="18px"
            src={
              index === slideIndex
                ? '/images/how-it-works-agents/heart-filled.svg'
                : '/images/how-it-works-agents/heart-empty.svg'
            }
            alt="navigation"
            draggable={false}
            userSelect="none"
            onClick={() => switchSlide(index)}
            cursor="pointer"
          />
        ),
      )}
    </Flex>
  );
};
